import { Component, ContentChild, EventEmitter, Input, Output, TemplateRef, ViewChild, OnDestroy } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { DomSanitizer } from '@angular/platform-browser'
import { TableConfig, TableMenuOptionsConfig } from '@app/models/shared.model'
import { CommonService } from '@app/shared/services/common.service'
import { DateService } from '@app/shared/services/date.service'
import { TranslateMessagesService } from '@app/shared/services/translate-messages.service'
import { TranslateService } from '@ngx-translate/core'
import { isArray } from 'lodash-es'
import { LazyLoadEvent, MenuItem } from 'primeng/api'
import { Table } from 'primeng/table'

@Component({ selector: 'app-yabi-table', templateUrl: './yabi-table.component.html', styleUrls: ['./yabi-table.component.scss'], standalone: false })
export class YabiTableComponent implements OnDestroy {
  /** Table reference for control table */
  @ViewChild('rowReference') rowReference: Table

  @ContentChild(TemplateRef) footer: TemplateRef<any> | null
  @ContentChild('summaryRow') summaryRow: TemplateRef<any>

  /** Filter global filed array use for search */
  @Input() globalFilterFields: string[] = []

  /** Table config */
  @Input() tableConfig: TableConfig

  /** Selected data */
  @Input() selectedData: Partial<any>[] = []

  /** Scroll height */
  @Input() scrollHeight: string

  /** Menu column width */
  @Input() menuColumnWidth: string = '100px'

  /** Icon callback function */
  @Input() iconCallbackFunction: (args: any) => any

  /** Main condition main column for set background color for row key */
  @Input() mainConditionColumnNameForSetBackgroundColorKey: string = 'active'

  /** Main condition main column for set background color for row value */
  @Input() mainConditionColumnNameForSetBackgroundColorValue: any = false

  /** Main condition main column , background color  */
  @Input() rowBackgroundColor: string = '#eff1fa'

  /**
   * Loose equality on purpose: callers pass mixed value types ('inactive', -1, false)
   * while row data may arrive as string or number from the API.
   */
  matchesRowBackgroundCondition(row: any): boolean {
    return row[this.mainConditionColumnNameForSetBackgroundColorKey] == this.mainConditionColumnNameForSetBackgroundColorValue
  }

  /** Table wrapper class */
  @Input() wrapperClass: string

  /** Using when we want binding cell with form group */
  @Input() formGroups: FormGroup[]

  /** Selected data changed */
  @Output() selectedDataChange: EventEmitter<Partial<any>[]> = new EventEmitter<Partial<any>[]>()

  /** On Action output emit when editable requests is true  */
  @Output() onAction = new EventEmitter<any>()

  /** On row cell click output */
  @Output() onRowCellClick = new EventEmitter<any>()

  /** Function for use for get data like labels etc... */
  @Input() labelCallbackFunction: (args: any) => any

  /** When the "virtualScrollLazyLoadOptions" are enabled and there lazy load changed event emit with last id */
  @Output() fetchLazyDataOutput = new EventEmitter<any>()

  /* Checkbox state */
  checkboxState: boolean | undefined

  /** For frozen disable when the lazy loading event is enabled */
  lazyLoadingEnabled: boolean = false

  lazyEvent: LazyLoadEvent

  /** When the table config enable the "virtualScrollLazyLoadOptions" check if the sort is new or not for fetching data */
  newSortHasDetected: boolean

  constructor(
    private translateMessagesService: TranslateMessagesService,
    private translate: TranslateService,
    public dateService: DateService,
    public commonService: CommonService,
    public sanitizer: DomSanitizer
  ) {}

  get columnsLength() {
    return this.tableConfig.columnsConfig?.length + (this.tableConfig.checkbox ? 2 : 0) + (this.tableConfig.buttonsColumn ? 1 : 0)
  }

  get dir() {
    return document.dir
  }

  /**
   * Row selected method, update checkbox state value and emit "selectedDataChange" output
   * @param event
   */
  rowSelectedOrUnselect() {
    if (!this.selectedData.length) this.checkboxState = undefined
    else {
      if (this.selectedData.length === this.tableConfig.data?.length) {
        this.checkboxState = true
      } else if (this.selectedData.length !== this.tableConfig.data?.length) {
        this.checkboxState = false
      } else this.checkboxState = undefined
    }
    this.selectedDataChange.emit(this.selectedData)
  }

  /**
   * Select/unselect all method, update selected events array and emit "selectedDataChange" output
   */
  onSelectAllChanged() {
    this.checkboxState = this.checkboxState === false ? undefined : this.checkboxState
    this.selectedData = this.checkboxState ? ([...this.tableConfig.data] as Partial<any>[]) : []
    this.selectedDataChange.emit(this.selectedData)
  }

  /**
   * Handle a click on a data cell. Always emits onRowCellClick (existing behaviour);
   * additionally, when tableConfig.rowClickActionName is set, emits onAction with that
   * action name so the whole row acts as the view/edit trigger.
   */
  onCellClick(row: any, col: any) {
    this.onRowCellClick.emit({ row, column: col })
    if (this.tableConfig?.rowClickActionName) {
      this.onAction.emit({ action: this.tableConfig.rowClickActionName, model: row })
    }
  }

  /**
   * Init menu items
   */
  initItems(row: any) {
    const items: MenuItem[] = []
    this.tableConfig.menuOptions?.forEach((v: TableMenuOptionsConfig) => {
      const hasConditionAndConditionIsTrue = this.checkIfMenuConditionHasTrue(v, row)
      if (hasConditionAndConditionIsTrue || v.valueCondition == null) {
        items.push({
          id: `row-${v.actionName}-${row.id}`,
          label: this.translate.instant(v.label ?? ''),
          icon: v.icon,
          iconClass: v.iconClass,
          styleClass: v.styleClass,
          command: () => {
            this.onAction.emit({
              action: v.actionName,
              model: this.selectedData,
            })
          },
        })
      }
    })
    return items
  }

  /**
   * Check if the me
   * @param v Menu option config
   * @param row Table row
   * @returns True if the menu condition has true
   */
  checkIfMenuConditionHasTrue(v: TableMenuOptionsConfig, row: any) {
    let hasConditionAndConditionIsTrue: any = true
    const keys = v?.keyCondition?.split(',')

    if (keys) {
      if (keys.length == 1) {
        if (v.operatorCondition && v.keyCondition && v.operatorCondition)
          switch (v.operatorCondition) {
            case '==':
              hasConditionAndConditionIsTrue = isArray(v.valueCondition) ? v?.valueCondition?.includes(row[v.keyCondition]) : row[v.keyCondition] === v.valueCondition
              break
            case '!=':
              hasConditionAndConditionIsTrue = isArray(v.valueCondition) ? !v?.valueCondition?.includes(row[v.keyCondition]) : row[v.keyCondition] !== v.valueCondition
              break
            default:
              break
          }
      } else {
        hasConditionAndConditionIsTrue = []
        keys.forEach((k: any, i: number) => {
          // multi-key mode: operatorCondition/valueCondition are parallel arrays
          const operators = v.operatorCondition as ('==' | '!=')[]
          const values = v.valueCondition as unknown[]
          const value = values[i]
          switch (operators[i]) {
            case '==':
              hasConditionAndConditionIsTrue[i] = isArray(value) ? value.includes(row[k]) : row[k] === value
              break
            case '!=':
              hasConditionAndConditionIsTrue[i] = isArray(value) ? value.includes(row[k]) : row[k] !== value
              break
            default:
              break
          }
        })

        if (hasConditionAndConditionIsTrue.every((u: boolean) => u == true)) {
          hasConditionAndConditionIsTrue = true
        } else hasConditionAndConditionIsTrue = false
      }
    }
    return hasConditionAndConditionIsTrue
  }

  isExpanded(row: any) {
    const groupBy = this.tableConfig.rowGroupOptions?.groupRowsBy ?? ''
    const a = this.tableConfig.data.filter((v: any) => v[groupBy] == row[groupBy])
    return a.findIndex((v: any) => v.id === row.id) > 0
  }

  showExpanded(row: any) {
    const groupBy = this.tableConfig.rowGroupOptions?.groupRowsBy ?? ''
    return this.tableConfig.data.filter((v: any) => v[groupBy] == row[groupBy] && v.id != row.id)?.length > 0
  }

  /**
   * When using lazy loading and there is loading event the "fetchLazyDataOutput" will emitting
   * @param event Lazy loading event
   */
  onLazyLoading(event: any) {
    this.lazyEvent = event
    const lastVisibleRowIndex = event.first + event.rows

    if (lastVisibleRowIndex == this.tableConfig?.data?.length) {
      if (this.newSortHasDetected) this.newSortHasDetected = false
      this.onLazyEvent()
    }
  }

  /**
   * When the virtualScrollLazyLoadOptions enabled the sort will emits "fetchLazyDataOutput"
   * @param e
   */
  sortLazyLoading(e: any) {
    this.newSortHasDetected = true

    if (this.tableConfig.columnsConfig.find((c) => c.name == e.field)?.sortable && this.tableConfig.virtualScrollLazyLoadOptions) {
      this.onLazyEvent()
      this.rowReference.scrollToVirtualIndex(this.tableConfig.virtualScrollLazyLoadOptions.rows - 1)
    }
  }

  /**
   * Emits "fetchLazyDataOutput" output only when the virtualScrollLazyLoadOptions enabled in table config
   */
  onLazyEvent() {
    this.lazyLoadingEnabled = true
    this.fetchLazyDataOutput.emit({
      lazyEvent: this.lazyEvent,
      newSortHasDetected: this.newSortHasDetected,
    })
    this.lazyLoadingEnabled = false
  }

  ngOnDestroy() {
    // Clean up any template references
    if (this.footer) {
      this.footer = null
    }
  }
}
